import { GetServerSideProps } from 'next';
import {
  Avatar,
  Box,
  Button,
  Circle,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextHead from 'next/head';
import React, { useEffect, useRef, useState } from 'react';
import isMobileDevice from 'ismobilejs';
import NextLink from 'next/link';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { title } from '../../common/libs/metadata';
import { getPublicRuntimeConfig } from '../../common/config';
import FullWidthBox from '../../components/FullWidthBox';
import { IoRadio } from '../../components/Icons';
import withAuth from '../../server/libs/withAuth';
import { generateS3DownloadUrl } from '../../server/libs/siteSettings';
import { getJsonFileFromS3 } from '../../server/libs/aws';
import { NextPageWithLayout } from '../../common/types';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import { useNewResidentsOrientationEventStatus } from '../../hooks/useNewResidentsOrientationEventStatus';
import { getVideo, Video } from '../../server/libs/videoArticles';
import VideoPlayerContent from '../../components/VideoPlayerContent';

const { idBaseUrl } = getPublicRuntimeConfig();

const GA_CATEGORY = 'new_residents_orientation';

export const SITE_SETTING_JSON_S3_KEY =
  'special_articles/new-residents-orientation/new-residents-orientation_settings.json';

const pagePath = '/events/new-residents-orientation';

const HEARTBEAT_INTERVAL_MS = 30_000;

const signInUrl = `/login?redirectUrl=${encodeURIComponent(pagePath)}`;
const signUpUrl = `${idBaseUrl}/signup?from_service=slide&mode=new-residents-orientation&redirect_url=${encodeURIComponent(
  pagePath,
)}`;

type Speaker = {
  id?: string;
  name: string;
  imageS3Key: string;
  hospital: string;
  award?: string;
};

type ScheduleRowProps = {
  start?: string;
  end?: string;
  title: string;
  article?: {
    articleId?: string;
    title: string;
  };
  video?: {
    linkUrl: string;
  };
  theme?: string;
  speaker?: Speaker;
};

type EnqueteSettings = {
  [key in EventStatus]?: {
    url: string;
    label: string;
    message?: string;
  };
};

export type LiveStreamSettings = {
  isVisible: boolean;
  youtubeVideoId: string;
  liveStreamTitle: string;
  streamStartAt: string;
  streamEndAt: string;
  manualStreamStatus: null | 'start' | 'stop';
  statusMessages: {
    onAir: string;
    finished: string;
  };
};

export type ArchiveVideoSettings = {
  isVisible: boolean;
  title: string;
  guestMessage: string;
  articleId: string;
  videoId: string;
  openAt: string;
  closeAt: string;
  video?: Video | null; // サーバーサイドでarticleId,videoIdを元にセットされる値
};

export type Settings = {
  startDateTime: string;
  endDateTime: string;
  headerImagePcS3Key: string;
  headerImageMobileS3Key: string;
  posterImageS3Key: string;
  liveStream: LiveStreamSettings;
  archiveVideo?: ArchiveVideoSettings;
  members: MemberProps[];
  scheduleLayout?: 'live' | 'archive';
  scheduleData: ScheduleRowProps[];
  enquete: EnqueteSettings | null;
};

type PageContext = {
  isLoggedIn: boolean;
  isMobile: boolean;
} & Settings;

type MemberProps = {
  name: string;
  imageS3Key: string;
  info: string[];
  comment: string;
};

export type EventStatus = 'before' | 'ongoing' | 'ended';

const Member = ({ name, imageS3Key, info, comment }: MemberProps) => (
  <Flex
    w="full"
    flexDirection={{
      base: 'column',
      md: 'row',
    }}
    gap={1}
  >
    <Flex
      gap={{
        base: 2,
        lg: 0,
      }}
      justifyContent="flex-start"
      alignItems="center"
      flexDirection={{
        base: 'row',
        lg: 'column',
      }}
      w={{
        base: '100%',
        lg: '40%',
      }}
      textAlign={{
        base: 'left',
      }}
    >
      <Box w="80px" flexShrink={0} pb={2}>
        <Image
          src={generateS3DownloadUrl(imageS3Key)}
          alt={`${name} アイコン`}
          w="full"
          borderRadius="full"
        />
      </Box>
      <Box>
        <Heading as="h4" fontSize="md">
          {name}
        </Heading>
        <Flex fontSize="xs" color="textGray" flexWrap="wrap">
          {info.map((line, index) => (
            <Box key={index} display="inline-block">
              {line}
            </Box>
          ))}
        </Flex>
      </Box>
    </Flex>
    <Box
      w={{
        base: '100%',
        lg: '60%',
      }}
    >
      <Text whiteSpace="pre-wrap" fontSize="sm">
        {comment}
      </Text>
    </Box>
  </Flex>
);

const YouTubeEmbed = ({
  videoId,
  title,
}: {
  videoId: string;
  title: string;
}) => (
  <Box w="full" mx="auto" position="relative">
    <Box paddingBottom="56.25%" position="relative" height={0}>
      <Box
        as="iframe"
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allowFullScreen
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
      />
    </Box>
    {/* 「後で見る」・「共有」ボタンへのクリックを遮断するオーバーレイ */}
    <Box
      position="absolute"
      top={0}
      right={0}
      w="250px"
      h="100px"
      zIndex={10}
    />
  </Box>
);

const VideoPlaceholder = ({
  posterImage,
  eventStatus,
  statusMessages,
  diff,
  isLoggedIn,
}: {
  posterImage: string;
  eventStatus: EventStatus;
  statusMessages: {
    onAir: string;
    finished: string;
  };
  diff: {
    days: number;
    hours: number;
    minutes: number;
  };
  isLoggedIn?: boolean;
}) => {
  let message = '';

  switch (eventStatus) {
    case 'before':
      if (diff.days > 0) {
        message = `${diff.days}日後に配信予定`;
      } else if (diff.hours > 0) {
        message = `${diff.hours}時間後に配信予定`;
      } else {
        message = `${diff.minutes}分後に配信予定`;
      }
      break;
    case 'ongoing':
      message = statusMessages.onAir;
      break;
    case 'ended':
      message = statusMessages.finished;
      break;
  }
  return (
    <Flex paddingBottom="56.25%" position="relative" w="full">
      <Image
        src={posterImage}
        alt="配信時ここに動画が表示される"
        objectFit="contain"
        objectPosition="center"
        position="absolute"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        display="flex"
        alignItems="center"
      >
        <Flex
          w={{
            base: 'auto',
            md: 'full',
          }}
          maxW={isLoggedIn ? '600px' : '700px'}
          mx="auto"
          py={6}
          px={{
            base: 14,
            md: 20,
          }}
          color="white"
          backgroundColor="rgba(26,32,44,0.9)"
          alignItems="center"
          borderRadius="lg"
          flexDirection={{
            base: 'column',
            md: 'row',
          }}
          gap={2}
          justifyContent={isLoggedIn ? 'center' : 'space-between'}
        >
          <Flex gap={4} alignItems="center">
            <IoRadio size={36} />
            <VStack spacing={1} alignItems="flex-start">
              <Text fontSize="xl" fontWeight="bold">
                {message}
              </Text>
              {eventStatus === 'before' && (
                <Text fontSize="md" fontWeight="bold">
                  4月4日(土) 13:00
                </Text>
              )}
            </VStack>
          </Flex>
          {!isLoggedIn && eventStatus !== 'ended' && (
            <Button py={6} colorScheme="brand" size="lg" flexDir="column">
              イベント参加はこちら
              <Text as="small" fontSize="xs" fontWeight="normal">
                ※Antaa 会員登録が必要です。
              </Text>
            </Button>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

const LoggedIn = ({
  eventStatus,
  statusMessages,
  posterImage,
  youtubeVideoId,
  liveStreamTitle,
  isLiveStreamOnAir,
  enquete,
  diff,
}: {
  eventStatus: EventStatus;
  statusMessages: {
    onAir: string;
    finished: string;
  };
  diff: {
    days: number;
    hours: number;
    minutes: number;
  };
  posterImage: string;
  youtubeVideoId: string | null;
  liveStreamTitle: string;
  isLiveStreamOnAir: boolean;
  enquete: EnqueteSettings;
}) => {
  const isLiveViewing = !!(youtubeVideoId && isLiveStreamOnAir);
  const visibleStartTime = useRef<number>(0);
  const totalVisibleMs = useRef<number>(0);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSentLeave = useRef(false);

  /**
   * ライブ配信の視聴状況を GA イベントで計測する。
   * - live_view: 配信ページ表示時に1回送信（視聴者の母数）
   * - live_visibility: タブの表示/非表示切替時に送信（視聴区間の開始・終了）
   * - live_heartbeat: タブが前面表示中のみ30秒間隔で送信（滞在時間の近似値）
   * - live_leave: ページ離脱時に累計visible秒数とともに1回送信
   *
   * 条件: ログイン済み かつ 配信中(isLiveStreamOnAir:true) かつ youtubeVideoId が設定されている場合のみ動作。
   * 注意: iframe制約により動画の再生/停止状態は取得できないため、
   *       ここで計測するのは「ページを前面で開いていた時間」の近似値である。
   */
  useEffect(() => {
    if (!isLiveViewing) return;

    const getVisibleSeconds = () => {
      let total = totalVisibleMs.current;
      if (
        document.visibilityState === 'visible' &&
        visibleStartTime.current > 0
      ) {
        total += Date.now() - visibleStartTime.current;
      }
      return Math.floor(total / 1000).toString();
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      heartbeatTimer.current = setInterval(() => {
        sendAnalyticsEvent(GA_CATEGORY, 'live_heartbeat', getVisibleSeconds());
      }, HEARTBEAT_INTERVAL_MS);
    };

    const stopHeartbeat = () => {
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        visibleStartTime.current = Date.now();
        sendAnalyticsEvent(GA_CATEGORY, 'live_visibility', 'visible');
        startHeartbeat();
      } else {
        if (visibleStartTime.current > 0) {
          totalVisibleMs.current += Date.now() - visibleStartTime.current;
          visibleStartTime.current = 0;
        }
        sendAnalyticsEvent(GA_CATEGORY, 'live_visibility', 'hidden');
        stopHeartbeat();
      }
    };

    const handleLeave = () => {
      if (hasSentLeave.current) return;
      hasSentLeave.current = true;
      if (visibleStartTime.current > 0) {
        totalVisibleMs.current += Date.now() - visibleStartTime.current;
        visibleStartTime.current = 0;
      }
      stopHeartbeat();
      sendAnalyticsEvent(
        GA_CATEGORY,
        'live_leave',
        getVisibleSeconds(),
        'beacon',
      );
    };

    // 初期化: live_view送信、visible状態なら計測開始
    sendAnalyticsEvent(GA_CATEGORY, 'live_view', 'start');
    if (document.visibilityState === 'visible') {
      visibleStartTime.current = Date.now();
      startHeartbeat();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handleLeave);
    window.addEventListener('beforeunload', handleLeave);

    return () => {
      handleLeave();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handleLeave);
      window.removeEventListener('beforeunload', handleLeave);
      stopHeartbeat();
    };
  }, [isLiveViewing]);

  const message = enquete[eventStatus]?.message || null;
  const enqueteFormUrl = enquete[eventStatus]?.url || null;
  const enqueteFormLabel = enquete[eventStatus]?.label || null;

  return (
    <>
      {youtubeVideoId && isLiveStreamOnAir ? (
        <YouTubeEmbed videoId={youtubeVideoId} title={liveStreamTitle} />
      ) : (
        <VideoPlaceholder
          posterImage={posterImage}
          eventStatus={eventStatus}
          statusMessages={statusMessages}
          diff={diff}
          isLoggedIn
        />
      )}
      {enqueteFormUrl && (
        <Box textAlign="center">
          <Button
            as="a"
            target="_blank"
            rel="noopener noreferrer"
            px={14}
            colorScheme="brand"
            size="lg"
            href={enqueteFormUrl}
            onClick={() => {
              sendAnalyticsEvent(
                GA_CATEGORY,
                'click',
                `enquete_${eventStatus}`,
              );
            }}
          >
            {enqueteFormLabel}
          </Button>
          <Text
            fontSize="sm"
            color="textGray"
            mt={4}
            textAlign={{
              base: 'left',
              lg: 'center',
            }}
          >
            {message}
          </Text>
        </Box>
      )}
    </>
  );
};

const NotLogin = ({
  eventStatus,
  statusMessages,
  diff,
  posterImage,
}: {
  eventStatus: EventStatus;
  statusMessages: {
    onAir: string;
    finished: string;
  };
  diff: {
    days: number;
    hours: number;
    minutes: number;
  };
  posterImage: string;
}) => (
  <>
    <Box w="full" mx="auto">
      <Link
        _hover={{
          textDecoration: 'none',
          opacity: 0.8,
        }}
        href={signUpUrl}
        onClick={() => {
          sendAnalyticsEvent(GA_CATEGORY, 'click', 'signup');
        }}
      >
        <VideoPlaceholder
          posterImage={posterImage}
          eventStatus={eventStatus}
          diff={diff}
          statusMessages={statusMessages}
        />
      </Link>
    </Box>
    {eventStatus !== 'ended' && (
      <Box textAlign="center">
        <Button
          as="a"
          px={14}
          colorScheme="brand"
          size="lg"
          href={signUpUrl}
          onClick={() => {
            sendAnalyticsEvent(GA_CATEGORY, 'click', 'signup');
          }}
        >
          イベント参加はこちら
        </Button>
        <Text
          fontSize={{
            base: 'xs',
            md: 'sm',
          }}
          color="textGray"
          mt={4}
          textAlign="center"
        >
          ※Antaa 会員登録が必要です。参加は医師・医学生限定となります。
        </Text>
        <Link href={signInUrl} mt={2}>
          <Text
            fontSize="sm"
            mt={2}
            textAlign={{
              base: 'left',
              sm: 'center',
            }}
            textDecoration="underline"
          >
            ＞すでにAntaaアカウントをお持ちの方はこちらからログイン
          </Text>
        </Link>
      </Box>
    )}
  </>
);

const SpeakerInfo = ({ name, hospital, award, imageS3Key }: Speaker) => (
  <HStack spacing={3} align="center">
    <Avatar size="md" name={name} src={generateS3DownloadUrl(imageS3Key)} />
    <VStack align="flex-start" spacing={1}>
      <HStack flexWrap="wrap" spacing={1} lineHeight={1}>
        <Text fontSize="lg" fontWeight="bold" color="primaryMain">
          {name}
        </Text>
        {award && (
          <Text
            as="span"
            fontSize={10}
            fontWeight="bold"
            color="white"
            bg="awardGold"
            px={2}
            py={1}
            borderRadius={3}
          >
            {award}
          </Text>
        )}
      </HStack>
      <Text fontSize="sm" color="textPrimary">
        {hospital}
      </Text>
    </VStack>
  </HStack>
);

const ScheduleRow = ({
  title,
  start,
  end,
  theme,
  speaker,
  isLast,
  article,
  video,
  layout = 'live',
}: ScheduleRowProps & {
  layout?: 'live' | 'archive';
  isLast: boolean;
}) => {
  let templateColumnsMd = undefined;
  switch (layout) {
    case 'live':
      templateColumnsMd = '120px 140px 2fr 3fr';
      break;
    case 'archive':
      templateColumnsMd = '110px 1fr 1fr 165px';
      break;
  }

  return (
    <Box position="relative">
      <>
        {/* スケジュールの時系列を表す装飾要素 */}
        {!isLast && (
          <Box
            position="absolute"
            left="5.5px"
            top={{
              base: 'calc(1.5em + 6px)',
              md: '50%',
            }}
            bottom={{
              base: 'calc(-1.5em - 6px)',
              md: '-50%',
            }}
            w="1px"
            bg="primaryMain"
          />
        )}
        <Circle
          position="absolute"
          top={{
            base: '1.5em',
            md: 'calc(50% - 6px)',
          }}
          size="12px"
          border="1px solid"
          borderColor="primaryDark"
          bg="primaryLight"
          zIndex={1}
        />
      </>
      <Grid
        marginLeft={7}
        templateColumns={{
          base: '1fr 1fr',
          md: templateColumnsMd,
        }}
        gap={4}
        alignItems="center"
        py={4}
        borderTop="1px solid"
        borderBottom={isLast ? '1px solid' : 'none'}
        borderColor="borderLight"
      >
        <Text fontWeight="bold" fontSize="md" textAlign="left">
          {title}
        </Text>
        {layout === 'live' && (
          <Text fontSize="lg">
            {!start && !end ? '' : `${start ?? ''} 〜 ${end ?? ''}`}
          </Text>
        )}
        <GridItem
          colSpan={{
            base: 2,
            sm: undefined,
          }}
        >
          {layout === 'live' && (
            <Text fontSize="lg" fontWeight="bold">
              {theme ? `テーマ「${theme}」` : ''}
            </Text>
          )}
          {layout === 'archive' &&
            article &&
            (article.articleId ? (
              <NextLink href={`/article/view/${article.articleId}`} passHref>
                <Link
                  onClick={() => {
                    sendAnalyticsEvent(
                      GA_CATEGORY,
                      'click',
                      `${article.articleId}`,
                    );
                  }}
                >
                  <Text fontSize="lg" fontWeight="bold">
                    {article.title}
                  </Text>
                </Link>
              </NextLink>
            ) : (
              <Text fontSize="lg" fontWeight="bold">
                {article.title}
              </Text>
            ))}
        </GridItem>
        <GridItem
          colSpan={{
            base: 2,
            sm: undefined,
          }}
        >
          {speaker ? (
            speaker?.id ? (
              <NextLink passHref href={`/profile/${speaker.id}`}>
                <Link _hover={{ textDecoration: 'none' }}>
                  <SpeakerInfo {...speaker} />
                </Link>
              </NextLink>
            ) : (
              <SpeakerInfo {...speaker} />
            )
          ) : theme ? (
            <Box py={2} pl={{ base: 1, lg: 14 }} ml={{ lg: 1 }}>
              <Text fontSize="md" color="textGray">
                調整中
              </Text>
            </Box>
          ) : (
            <HStack spacing={3}>
              <Avatar
                size="md"
                name="Antaa"
                src="/images/icon_antaa_white_small.png"
                backgroundColor="backgroundMedium"
              />
              <Text fontSize="lg">Antaa司会</Text>
            </HStack>
          )}
        </GridItem>
        {layout === 'archive' && (
          <GridItem
            colSpan={{ base: 2, sm: undefined }}
            textAlign={{
              base: 'left',
              sm: 'center',
            }}
          >
            {video?.linkUrl && (
              <>
                <Link
                  fontWeight="bold"
                  href={video.linkUrl}
                  isExternal
                  onClick={() => {
                    sendAnalyticsEvent(
                      GA_CATEGORY,
                      'click_link',
                      video.linkUrl,
                    );
                  }}
                >
                  <Text color="textLink" textDecoration="underline">
                    動画をみる <ExternalLinkIcon />
                  </Text>
                </Link>
                <Text color="textGray" fontSize="xs">
                  （日経メディカルのサイトへ）
                </Text>
              </>
            )}
          </GridItem>
        )}
      </Grid>
    </Box>
  );
};

const ArchiveVideo = ({
  title,
  video,
  openAt,
  closeAt,
  guestMessage,
  isLoggedIn,
}: ArchiveVideoSettings & { isLoggedIn: boolean }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!video) return;

    // 1分ごとに状態を更新
    const updateIsOpen = () => {
      const now = Date.now();
      setIsOpen(
        new Date(openAt).getTime() <= now && now <= new Date(closeAt).getTime(),
      );
    };

    updateIsOpen();
    const timer = setInterval(updateIsOpen, 1000 * 60);

    return () => clearInterval(timer);
  }, [closeAt, openAt, video]);

  if (!isOpen || !video) return null;

  if (!isLoggedIn)
    return (
      <Box mb={10}>
        <Box w="full" mx="auto" mb={4}>
          <Link
            _hover={{
              textDecoration: 'none',
              opacity: 0.8,
            }}
            href={signInUrl}
            onClick={() => {
              sendAnalyticsEvent(GA_CATEGORY, 'click', 'signin');
            }}
          >
            <Flex paddingBottom="56.25%" position="relative" w="full">
              <Image
                src={video.poster}
                alt="アーカイブ動画サムネイル"
                objectFit="contain"
                objectPosition="center"
                position="absolute"
              />
              <Box
                position="absolute"
                top={0}
                left={0}
                w="full"
                h="full"
                display="flex"
                alignItems="center"
              >
                <Flex
                  w={{
                    base: 'auto',
                    md: 'full',
                  }}
                  maxW="720px"
                  mx="auto"
                  py={6}
                  px={8}
                  color="white"
                  backgroundColor="rgba(26,32,44,0.9)"
                  alignItems="center"
                  borderRadius="lg"
                  flexDirection={{
                    base: 'column',
                    md: 'row',
                  }}
                  gap={2}
                  justifyContent="space-between"
                >
                  <Flex gap={4} alignItems="center">
                    <VStack spacing={1} alignItems="flex-start">
                      <Text
                        fontSize={{
                          base: 'md',
                          md: 'lg',
                        }}
                        fontWeight="bold"
                        whiteSpace="pre-wrap"
                      >
                        {guestMessage}
                      </Text>
                    </VStack>
                  </Flex>
                  <Button
                    as="span"
                    py={6}
                    colorScheme="brand"
                    size={{
                      base: 'md',
                      md: 'lg',
                    }}
                    flexDir="column"
                    minW="320px"
                  >
                    ログインしてアーカイブ動画をみる
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </Link>
        </Box>
        <Box textAlign="center">
          <Button
            as="a"
            px={6}
            colorScheme="brand"
            size="lg"
            href={signInUrl}
            onClick={() => {
              sendAnalyticsEvent(GA_CATEGORY, 'click', 'signin');
            }}
          >
            ログインしてアーカイブ動画をみる
          </Button>
          <Text
            fontSize={{
              base: 'xs',
              md: 'sm',
            }}
            color="textGray"
            mt={4}
            textAlign="center"
          >
            ※Antaa 会員登録が必要です。参加は医師・医学生限定となります。
          </Text>
        </Box>
      </Box>
    );

  return (
    <Box mb={10}>
      <Heading
        as="h3"
        size={{
          base: 'md',
          lg: 'lg',
        }}
        mb={4}
      >
        {title}
      </Heading>
      <VideoPlayerContent
        {...video}
        onPlay={() => {
          sendAnalyticsEvent(GA_CATEGORY, 'play', 'archive_video');
        }}
        videoOnly
      />
    </Box>
  );
};

const NewResidentsOrientationPage: NextPageWithLayout<PageContext> = ({
  isMobile,
  isLoggedIn,
  startDateTime,
  endDateTime,
  headerImagePcS3Key,
  headerImageMobileS3Key,
  posterImageS3Key,
  liveStream,
  enquete,
  members,
  scheduleLayout,
  scheduleData,
  archiveVideo,
}: PageContext) => {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const canonicalUrl = `${slideWebBaseUrl}${pagePath}`;

  const { liveStreamSettings, isLiveStreamOnAir, eventStatus, diff } =
    useNewResidentsOrientationEventStatus({
      isLoggedIn,
      startDateTime,
      endDateTime,
      initialLiveStreamSettings: liveStream,
    });

  return (
    <>
      <NextHead>
        <title>
          {title(
            'Antaa 人気スライド講師が教える 2026年度 新・研修医向け 1dayオリエンテーション 4月4日（土）13時～ オンライン配信開始！',
          )}
        </title>
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <Box>
        <Image
          src={
            isMobile
              ? generateS3DownloadUrl(headerImageMobileS3Key)
              : generateS3DownloadUrl(headerImagePcS3Key)
          }
          alt="Antaa 人気スライド講師が教える 2026年度 新・研修医向け 1dayオリエンテーション"
          w="full"
        />
      </Box>
      <FullWidthBox bg="#EEF6ED" py={6}>
        <Container>
          <Heading
            as="h3"
            fontSize="xl"
            color="primaryDark"
            mb={6}
            textAlign={{
              base: 'left',
              lg: 'center',
            }}
          >
            この春、新しく医師になるみなさまへ〔日経メディカル×Antaa
            新研修医応援企画〕
          </Heading>
          <SimpleGrid
            columns={{
              base: 1,
              lg: members.length,
            }}
            spacing={4}
            p={4}
            backgroundColor="white"
            mx={{
              base: 0,
              lg: -2,
            }}
          >
            {members.map((member, index) => (
              <Member key={index} {...member} />
            ))}
          </SimpleGrid>
        </Container>
      </FullWidthBox>
      <Container py={12}>
        {liveStreamSettings?.isVisible && (
          <VStack spacing={6} mb={6} align="center">
            {isLoggedIn && enquete && liveStreamSettings ? (
              <LoggedIn
                isLiveStreamOnAir={isLiveStreamOnAir}
                youtubeVideoId={liveStreamSettings?.youtubeVideoId}
                liveStreamTitle={liveStreamSettings.liveStreamTitle}
                enquete={enquete}
                eventStatus={eventStatus}
                statusMessages={liveStreamSettings?.statusMessages}
                posterImage={generateS3DownloadUrl(posterImageS3Key)}
                diff={diff}
              />
            ) : (
              <NotLogin
                diff={diff}
                eventStatus={eventStatus}
                statusMessages={liveStreamSettings?.statusMessages}
                posterImage={generateS3DownloadUrl(posterImageS3Key)}
              />
            )}
          </VStack>
        )}
        {archiveVideo?.isVisible && (
          <ArchiveVideo {...archiveVideo} isLoggedIn={isLoggedIn} />
        )}
        <HStack mb={6} borderLeft="4px solid" borderColor="green.500" pl={3}>
          <Heading as="h2" size="md">
            タイムスケジュール
          </Heading>
        </HStack>
        <Box>
          {scheduleData.map((item, index) => {
            const isLast = index === scheduleData.length - 1;
            return (
              <ScheduleRow
                key={index}
                {...item}
                isLast={isLast}
                layout={scheduleLayout}
              />
            );
          })}
        </Box>
        <Text fontSize="sm" color="textGray" p={4} textAlign="center">
          ※内容は変更になる場合があります。
        </Text>
      </Container>
      <FullWidthBox bg="#EEF6ED" py={6}>
        <Container>
          <Heading
            as="h4"
            fontSize="xl"
            color="primaryDark"
            mb={6}
            textAlign="center"
          >
            講義の合間や振り返りに活用しよう
          </Heading>
          <Flex
            color="white"
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: 'column', md: 'row' }}
            backgroundColor="white"
            p={4}
          >
            <Image
              src="/images/special/kenshui30_top_title.png"
              alt="研修医1年目 必見スライド30選 先輩医師が「1年目で役立った」スライドを紹介"
              width={496}
              maxW={{ base: '100%', md: '48%' }}
            />
            <Image
              src={
                isMobile
                  ? '/images/special/kenshui30_top_illustration_sm.png'
                  : '/images/special/kenshui30_top_illustration_pc.png'
              }
              alt="全6カテゴリ/30スライドを厳選して紹介！「これだけは見ておきたい」をまとめました！"
              width={523}
              maxW={{ base: '100%', md: '52%' }}
            />
          </Flex>
          <Box textAlign="center" mt={6}>
            <NextLink href={'/special/kenshui30'} passHref>
              <Button
                px={14}
                colorScheme="brand"
                size="lg"
                onClick={() => {
                  sendAnalyticsEvent(GA_CATEGORY, 'click', 'view_kenshui30');
                }}
              >
                スライドを見る
              </Button>
            </NextLink>
          </Box>
        </Container>
      </FullWidthBox>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const isLoggedIn = context.req.user?.id !== undefined;
    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;

    let settings: Settings | null = null;
    try {
      settings = (await getJsonFileFromS3(
        SITE_SETTING_JSON_S3_KEY,
      )) as Settings;
    } catch (e) {
      console.error('サイト設定の取得に失敗しました。', e);
    }

    if (!settings) {
      return {
        notFound: true,
      };
    }

    let video: Video | null = null;
    if (
      settings?.archiveVideo?.isVisible &&
      settings?.archiveVideo?.articleId &&
      settings?.archiveVideo?.videoId
    ) {
      video = await getVideo(
        settings.archiveVideo.articleId,
        settings.archiveVideo.videoId,
      );
      settings.archiveVideo.video = video;
      // 非ログインの場合は動画パスを削除
      if (settings.archiveVideo.video && !isLoggedIn) {
        settings.archiveVideo.video.videoUrl = '';
      }
    }

    return {
      props: {
        isLoggedIn,
        isMobile,
        ...settings,
        // 非ログイン時に隠す値
        enquete: isLoggedIn ? settings.enquete : null,
      },
    };
  },
);

export default NewResidentsOrientationPage;
